import { Controller } from '@hotwired/stimulus';
import { getCableConsumer } from '../channels/consumer';

export default class extends Controller {
  static targets = [
    'backdrop',
    'body',
    'messages',
    'emptyState',
    'input',
    'status',
    'composer',
    'closeButton',
    'offlineFlow',
    'topicStep',
    'followUpStep',
    'followUpPrompt',
    'videoFields',
    'appsScriptFields',
    'doingInput',
    'lookingForInput',
    'automateInput',
    'charCount',
    'historyNotice',
    'sendButton'
  ];

  static values = {
    conversationUrl: String,
    readUrl: String,
    messagesUrl: String,
    newConversationUrl: String,
    pageUrl: String,
    formulaUrl: String,
    realtimeMode: { type: String, default: 'cable' },
    wizardOnline: Boolean,
    unreadCount: Number,
    pollIntervalOpen: { type: Number, default: 4000 },
    pollIntervalClosed: { type: Number, default: 60000 },
    maxLength: { type: Number, default: 1000 },
    userAvatarUrl: String,
    wizardAvatarUrl: String
  };

  connect() {
    this.opened = false;
    this.unread = this.hasUnreadCountValue ? Math.max(0, this.unreadCountValue) : 0;
    this.subscription = null;
    this.pollTimer = null;
    this.pollingActive = false;
    this.selectedTopic = null;
    this.boundToggle = () => this.toggle();
    this.boundEscape = (event) => {
      if (event.key === 'Escape') this.close();
    };
    this.boundVisibilityChange = this.onVisibilityChange.bind(this);
    window.addEventListener('wizard-widget:toggle', this.boundToggle);
    document.addEventListener('keydown', this.boundEscape);
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
    if (this.realtimeModeValue === 'poll') {
      this.startPolling();
    } else {
      this.bindCable();
    }
    this.syncWandTriggers();
    this.updateBadges();
    this.updateCharCount();
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    this.subscription?.unsubscribe();
    this.stopPolling();
    window.removeEventListener('wizard-widget:toggle', this.boundToggle);
    document.removeEventListener('keydown', this.boundEscape);
  }

  bindCable() {
    this.subscription = getCableConsumer().subscriptions.create(
      { channel: 'Wizard::ChatChannel' },
      {
        received: (data) => this.onCableMessage(data)
      }
    );
  }

  startPolling() {
    this.pollingActive = true;
    this.clearPollTimer();
    this.pollConversation();
    this.schedulePoll();
  }

  stopPolling() {
    this.pollingActive = false;
    this.clearPollTimer();
  }

  clearPollTimer() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  schedulePoll() {
    if (document.hidden) return;

    const delay = this.isOpen() ? this.pollIntervalOpenValue : this.pollIntervalClosedValue;
    this.pollTimer = setTimeout(() => this.pollConversation(), delay);
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.clearPollTimer();
      return;
    }

    if (this.pollingActive && this.realtimeModeValue === 'poll') {
      this.pollConversation();
    }
  }

  async pollConversation() {
    try {
      const url = new URL(this.conversationUrlValue, window.location.origin);
      url.searchParams.set('page_url', this.pageUrlValue);

      const lastId = this.latestMessageId();
      if (lastId) url.searchParams.set('since_message_id', String(lastId));

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });

      if (!response.ok) return;

      const data = await response.json();
      this.applyPollPayload(data);
    } catch {
      // ignore transient network errors
    } finally {
      if (this.pollingActive) this.schedulePoll();
    }
  }

  applyPollPayload(data) {
    const hadMessages = this.latestMessageId() !== null;

    if (typeof data.wizard_online === 'boolean') {
      this.wizardOnlineValue = data.wizard_online;
    }

    if (!hadMessages && (data.messages || []).length > 0) {
      this.renderConversation(data);
      return;
    }

    const wasNearBottom = this.isNearBottom();
    let appended = false;

    (data.messages || []).forEach((message) => {
      if (this.hasMessage(message.id)) return;

      this.appendMessage(message);
      appended = true;

      if (this.isUnreadAdminMessage(message)) {
        if (this.opened) {
          this.markConversationRead();
        } else {
          this.setUnreadCount(this.unread + 1);
        }
      }
    });

    if (typeof data.unread_count === 'number' && !this.opened) {
      this.setUnreadCount(data.unread_count);
    }

    if (this.opened && appended && wasNearBottom) this.scrollMessages();
  }

  latestMessageId() {
    const nodes = this.messagesTarget.querySelectorAll('[data-message-id]');
    if (!nodes.length) return null;

    return Math.max(...[...nodes].map((node) => Number(node.dataset.messageId)));
  }

  async toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      await this.open();
    }
  }

  async open() {
    this.element.classList.add('wizard-widget--open');
    this.backdropTarget.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('wizard-widget-open');
    this.opened = true;
    this.syncWandTriggers();
    await this.loadConversation();
    await this.markConversationRead();
    this.scrollMessages();
    this.focusActiveInput();
  }

  close() {
    this.element.classList.remove('wizard-widget--open');
    this.backdropTarget.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('wizard-widget-open');
    this.opened = false;
    this.selectedTopic = null;
    this.syncWandTriggers();
  }

  closeOnBackdrop(event) {
    if (event.target === this.backdropTarget) this.close();
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  isOpen() {
    return this.element.classList.contains('wizard-widget--open');
  }

  syncWandTriggers() {
    const expanded = this.isOpen();
    const offline = !this.wizardOnlineValue;
    const hasUnread = this.unread > 0;

    this.element.classList.toggle('wizard-widget--offline-panel', offline);
    this.updatePresenceStatus();

    document.querySelectorAll('[data-wizard-wand-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      trigger.classList.toggle('wizard-wand--active', expanded);
      trigger.classList.toggle('wizard-wand--offline', offline);
      trigger.classList.toggle('wizard-wand--unread', hasUnread);
      trigger.setAttribute(
        'aria-label',
        hasUnread
          ? `Get Sheets Help from the Wizard (${this.unread} unread ${this.unread === 1 ? 'message' : 'messages'})`
          : 'Get Sheets Help from the Wizard'
      );
    });
  }

  wizardOnlineValueChanged() {
    this.syncWandTriggers();
    this.updateBadges();
  }

  async loadConversation() {
    const url = new URL(this.conversationUrlValue, window.location.origin);
    url.searchParams.set('page_url', this.pageUrlValue);

    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });

    if (!response.ok) return;

    const data = await response.json();
    this.renderConversation(data);
  }

  renderConversation(data) {
    this.wizardOnlineValue = data.wizard_online;
    if (!this.opened && typeof data.unread_count === 'number') {
      this.setUnreadCount(data.unread_count);
    }
    this.updateHistoryNotice(data);
    const messageCount = (data.messages || []).length;
    this.messagesTarget.innerHTML = '';
    (data.messages || []).forEach((message) => this.appendMessage(message));

    if (data.wizard_online) {
      this.showOnlineMode(messageCount);
    } else if (messageCount === 0) {
      this.showOfflineTopicPicker();
    } else {
      this.showOfflineComposerMode(messageCount);
    }
    this.updateCharCount();
  }

  updateHistoryNotice(data) {
    if (!this.hasHistoryNoticeTarget) return;

    if (data.messages_truncated) {
      const total = data.messages_total_count || 0;
      const shown = (data.messages || []).length;
      this.historyNoticeTarget.textContent = `Showing the latest ${shown} of ${total} messages. Start a new conversation to clear older history from this view.`;
      this.historyNoticeTarget.classList.remove('hidden');
    } else {
      this.historyNoticeTarget.textContent = '';
      this.historyNoticeTarget.classList.add('hidden');
    }
  }

  updateCharCount() {
    if (!this.hasCharCountTarget || !this.hasInputTarget) return;

    const max = this.maxLengthValue;
    const length = this.inputTarget.value.length;
    const formattedMax = max.toLocaleString();
    this.charCountTarget.textContent = `${length.toLocaleString()} / ${formattedMax}`;
    this.charCountTarget.classList.toggle('wizard-widget__char-count--limit', length >= max);

    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = length === 0 || length > max;
    }
  }

  async startNewConversation(event) {
    event.preventDefault();
    const confirmed = window.confirm(
      'Start a new conversation? This chat will begin fresh.'
    );
    if (!confirmed) return;

    try {
      const response = await fetch(this.newConversationUrlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({ page_url: this.pageUrlValue })
      });

      if (!response.ok) throw new Error('new conversation failed');

      const data = await response.json();
      this.renderConversation(data);
      this.scrollMessages();
      this.focusActiveInput();
    } catch {
      window.alert('Could not start a new conversation. Please try again.');
    }
  }

  updatePresenceStatus() {
    if (!this.hasStatusTarget) return;

    const offline = !this.wizardOnlineValue;
    if (offline) {
      this.statusTarget.innerHTML =
        '<span class="wizard-widget__presence wizard-widget__presence--offline" aria-hidden="true"></span>' +
        "Leave a message. I'll reply here when I'm back.";
    } else {
      this.statusTarget.innerHTML =
        '<span class="wizard-widget__presence wizard-widget__presence--online" aria-hidden="true"></span>' +
        'The Wizard is online. Ask me anything.';
    }
  }

  updateEmptyState(show) {
    if (!this.hasEmptyStateTarget) return;

    this.emptyStateTarget.classList.toggle('hidden', !show);
    this.emptyStateTarget.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  setIntakeMode(active) {
    if (!this.hasBodyTarget) return;

    this.bodyTarget.classList.toggle('wizard-widget__body--intake', active);
  }

  showOnlineMode(messageCount = 0) {
    if (this.hasOfflineFlowTarget) this.offlineFlowTarget.classList.add('hidden');
    if (this.hasComposerTarget) this.composerTarget.classList.remove('hidden');
    this.setIntakeMode(false);
    this.messagesTarget.classList.remove('hidden');
    this.updateEmptyState(messageCount === 0);
    this.resetFollowUp();
  }

  showOfflineTopicPicker() {
    if (!this.hasOfflineFlowTarget) return;

    this.offlineFlowTarget.classList.remove('hidden');
    this.topicStepTarget.classList.remove('hidden');
    this.followUpStepTarget.classList.add('hidden');
    if (this.hasComposerTarget) this.composerTarget.classList.add('hidden');
    this.setIntakeMode(true);
    this.messagesTarget.classList.add('hidden');
    this.updateEmptyState(false);
    this.selectedTopic = null;
    this.resetFollowUpFields();
  }

  showOfflineComposerMode(messageCount = 1) {
    if (this.hasOfflineFlowTarget) this.offlineFlowTarget.classList.add('hidden');
    if (this.hasComposerTarget) this.composerTarget.classList.remove('hidden');
    this.setIntakeMode(false);
    this.messagesTarget.classList.remove('hidden');
    this.updateEmptyState(messageCount === 0);
    this.resetFollowUp();
  }

  chooseTopic(event) {
    const topic = event.currentTarget.dataset.wizardTopic;
    if (!topic) return;

    if (topic === 'formula') {
      window.location.assign(this.formulaUrlValue);
      return;
    }

    if (topic === 'other') {
      this.showOfflineComposerMode(0);
      if (this.hasOfflineFlowTarget) this.offlineFlowTarget.classList.add('hidden');
      this.inputTarget.placeholder = 'Describe what you need help with…';
      this.focusActiveInput();
      return;
    }

    this.selectedTopic = topic;
    this.topicStepTarget.classList.add('hidden');
    this.followUpStepTarget.classList.remove('hidden');

    if (topic === 'video') {
      this.followUpPromptTarget.textContent =
        'Share a bit of context. I’ll suggest videos.';
      this.videoFieldsTarget.classList.remove('hidden');
      this.appsScriptFieldsTarget.classList.add('hidden');
      this.focusActiveInput();
      return;
    }

    if (topic === 'apps_script') {
      this.followUpPromptTarget.textContent = 'What are you trying to automate?';
      this.videoFieldsTarget.classList.add('hidden');
      this.appsScriptFieldsTarget.classList.remove('hidden');
      this.focusActiveInput();
    }
  }

  backToTopics(event) {
    event.preventDefault();
    this.showOfflineTopicPicker();
  }

  async submitFollowUp(event) {
    event.preventDefault();

    let body;
    if (this.selectedTopic === 'video') {
      const doing = this.doingInputTarget.value.trim();
      const lookingFor = this.lookingForInputTarget.value.trim();
      if (!doing || !lookingFor) return;

      body = [
        '[Wizard help: Which video to watch]',
        '',
        `What I'm doing: ${doing}`,
        `What I'm looking to do: ${lookingFor}`
      ].join('\n');
    } else if (this.selectedTopic === 'apps_script') {
      const automate = this.automateInputTarget.value.trim();
      if (!automate) return;

      body = ['[Wizard help: Apps Script]', '', `Trying to automate: ${automate}`].join('\n');
    } else {
      return;
    }

    if (!this.bodyWithinLimit(body)) return;

    await this.sendMessage(body, { clearFollowUp: true });
    this.appendSystemNote("Got it. We'll reply in this chat when we can.");
    this.showOfflineComposerMode(1);
  }

  handleEnter(event) {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;

    const form = event.target.closest('form');
    if (!form) return;

    event.preventDefault();

    if (
      this.hasDoingInputTarget &&
      event.target === this.doingInputTarget &&
      this.hasLookingForInputTarget &&
      !this.lookingForInputTarget.value.trim()
    ) {
      this.lookingForInputTarget.focus();
      return;
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  async send(event) {
    event.preventDefault();
    const body = this.inputTarget.value.trim();
    if (!body) return;

    const prefix = this.selectedTopic === 'other' ? '[Wizard help: Other]\n\n' : '';
    const payload = prefix + body;
    if (!this.bodyWithinLimit(payload)) return;

    await this.sendMessage(payload, { clearComposer: true });
  }

  bodyWithinLimit(body) {
    const max = this.maxLengthValue;
    if (body.length <= max) return true;

    window.alert(`Messages are limited to ${max.toLocaleString()} characters. Shorten your message and try again.`);
    return false;
  }

  async sendMessage(body, { clearComposer = false, clearFollowUp = false } = {}) {
    if (clearComposer) {
      this.inputTarget.value = '';
      this.updateCharCount();
    }
    this.inputTarget.disabled = true;

    try {
      const response = await fetch(this.messagesUrlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({
          message: { body },
          page_url: this.pageUrlValue
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const message = payload.errors?.join(' ') || 'Could not send your message. Please try again.';
        throw new Error(message);
      }

      const message = await response.json();
      this.appendMessage(message);
      this.scrollMessages();

      if (clearFollowUp) {
        this.resetFollowUpFields();
        this.selectedTopic = null;
      }
    } catch (error) {
      if (clearComposer) {
        this.inputTarget.value = body.replace(/^\[Wizard help: Other\]\n\n/, '');
        this.updateCharCount();
      }
      window.alert(error.message || 'Could not send your message. Please try again.');
    } finally {
      this.inputTarget.disabled = false;
      this.focusActiveInput();
    }
  }

  appendSystemNote(text) {
    const note = document.createElement('p');
    note.className = 'wizard-widget__system-note';
    note.textContent = text;
    this.messagesTarget.appendChild(note);
    this.scrollMessages();
  }

  resetFollowUp() {
    if (!this.hasFollowUpStepTarget) return;

    this.followUpStepTarget.classList.add('hidden');
    if (this.hasTopicStepTarget) this.topicStepTarget.classList.remove('hidden');
    this.resetFollowUpFields();
  }

  resetFollowUpFields() {
    if (this.hasDoingInputTarget) this.doingInputTarget.value = '';
    if (this.hasLookingForInputTarget) this.lookingForInputTarget.value = '';
    if (this.hasAutomateInputTarget) this.automateInputTarget.value = '';
    if (this.hasVideoFieldsTarget) this.videoFieldsTarget.classList.add('hidden');
    if (this.hasAppsScriptFieldsTarget) this.appsScriptFieldsTarget.classList.add('hidden');
  }

  focusActiveInput() {
    if (!this.opened) return;

    if (this.hasComposerTarget && !this.composerTarget.classList.contains('hidden')) {
      this.inputTarget.focus();
      return;
    }

    if (this.hasFollowUpStepTarget && !this.followUpStepTarget.classList.contains('hidden')) {
      if (this.selectedTopic === 'video' && this.hasDoingInputTarget) {
        this.doingInputTarget.focus();
      } else if (this.selectedTopic === 'apps_script' && this.hasAutomateInputTarget) {
        this.automateInputTarget.focus();
      }
    }
  }

  onCableMessage(data) {
    if (data.type !== 'message' || !data.message) return;
    this.ingestIncomingMessage(data.message);
  }

  ingestIncomingMessage(message) {
    if (this.hasMessage(message.id)) return;

    const wasNearBottom = this.isNearBottom();
    this.appendMessage(message);

    if (this.isUnreadAdminMessage(message)) {
      if (this.opened) {
        this.markConversationRead();
      } else {
        this.setUnreadCount(this.unread + 1);
      }
    }

    if (this.opened && wasNearBottom) this.scrollMessages();
  }

  isUnreadAdminMessage(message) {
    return message.sender_role === 'admin' && message.mine === false;
  }

  setUnreadCount(count) {
    this.unread = Math.max(0, count);
    this.updateBadges();
  }

  async markConversationRead() {
    try {
      const response = await fetch(this.readUrlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken()
        }
      });

      if (!response.ok) return;

      const data = await response.json();
      if (typeof data.unread_count === 'number') {
        this.setUnreadCount(data.unread_count);
      }
    } catch {
      // ignore, badge may refresh on next page load
    }
  }

  appendMessage(message) {
    if (this.hasMessage(message.id)) return;

    this.updateEmptyState(false);
    this.messagesTarget.appendChild(this.buildMessageElement(message));
  }

  buildMessageElement(message) {
    const mine = Boolean(message.mine);
    const fromWizard = message.sender_role === 'admin';

    const row = document.createElement('div');
    row.dataset.messageId = String(message.id);
    row.className = `wizard-chat__message wizard-chat__message--${mine ? 'mine' : 'theirs'}`;

    const avatarWrap = document.createElement('div');
    avatarWrap.className = 'wizard-chat__avatar';
    avatarWrap.setAttribute('aria-hidden', 'true');

    const avatarImg = document.createElement('img');
    avatarImg.className = `wizard-chat__avatar-img${fromWizard ? ' wizard-chat__avatar-img--wizard' : ''}`;
    avatarImg.src = fromWizard ? this.wizardAvatarUrlValue : this.userAvatarUrlValue;
    avatarImg.alt = fromWizard ? 'The Wizard' : '';
    avatarImg.loading = 'lazy';
    avatarWrap.appendChild(avatarImg);

    const bubble = document.createElement('div');
    bubble.className = `wizard-chat__bubble wizard-chat__bubble--${mine ? 'mine' : 'theirs'}`;

    const body = document.createElement('p');
    body.className = 'wizard-chat__body whitespace-pre-wrap';
    body.textContent = message.body;

    const time = document.createElement('time');
    time.className = 'wizard-chat__time';
    time.dateTime = message.created_at;
    time.textContent = this.formatTime(message.created_at);

    bubble.append(body, time);
    row.append(avatarWrap, bubble);

    return row;
  }

  hasMessage(id) {
    return Boolean(this.messagesTarget.querySelector(`[data-message-id="${id}"]`));
  }

  scrollMessages() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  // Only follow new messages when the reader is already at the bottom,
  // so scrolling up to read a long reply doesn't get yanked back down.
  isNearBottom() {
    const el = this.messagesTarget;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  updateBadges() {
    const hasUnread = Number(this.unread) > 0;
    const label = hasUnread
      ? this.unread > 9
        ? '9+'
        : String(this.unread)
      : '';

    this.syncWandTriggers();

    document.querySelectorAll('[data-wizard-wand-badge]').forEach((badge) => {
      if (hasUnread) {
        badge.textContent = label;
        badge.classList.add('wizard-wand__badge--notify');
        badge.classList.remove('hidden');
        badge.setAttribute('aria-hidden', 'false');
      } else {
        badge.textContent = '';
        badge.classList.remove('wizard-wand__badge--notify');
        badge.classList.add('hidden');
        badge.setAttribute('aria-hidden', 'true');
      }
    });
  }

  formatTime(iso) {
    const date = new Date(iso);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
